import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { setAuthToken } from '../api/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Check if we're processing an OIDC callback (has code and state params)
const isOidcCallback = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') && params.has('state');
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, login, accessToken } = useAuth();
  const [hasTriedLogin, setHasTriedLogin] = useState(false);
  const lastTokenRef = useRef<string | null>(null);

  // Set auth token SYNCHRONOUSLY during render, BEFORE children are rendered
  // This ensures the token is set before any child useEffect runs
  if (accessToken !== lastTokenRef.current) {
    lastTokenRef.current = accessToken || null;
    if (accessToken) {
      setAuthToken(accessToken);
    } else {
      setAuthToken(null);
    }
  }

  // Auto-redirect to login if not authenticated
  // But don't redirect if we're processing an OIDC callback
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isOidcCallback() && !hasTriedLogin) {
      setHasTriedLogin(true);
      login();
    }
  }, [isLoading, isAuthenticated, login, hasTriedLogin]);

  // Only render children when authenticated AND token is available
  if (isLoading || !isAuthenticated || !accessToken) {
    const statusText = isOidcCallback()
      ? 'Anmeldung wird verarbeitet...'
      : 'Weiterleitung zur Anmeldung...';

    return (
      <div className="loading-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem',
        backgroundColor: '#F5F7FA'
      }}>
        <div className="loading-spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #006355',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p>{statusText}</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
